import { api } from './client';
import type { DocumentUploadItem } from '../constants/loanDocumentTypes';

const EXTERNAL_API_KEY = 'sk-smedco-external-2024-12345';

// Create Loan Application with optional attachments (multipart, transactional on backend)
export async function createLoanApplicationWithAttachments(
  payload: any,
  files: DocumentUploadItem[] | File[] = []
): Promise<{ id: number; trackingNumber?: number; loanId: string }>{
  // First, create the loan application without attachments
  const loanResponse = await createLoanApplicationJson(payload);
  const { id, trackingNumber, loanId } = loanResponse;

  // Upload attachments in batches (internal endpoint). Backend enforces requirement at finalize.
  if (id && files.length > 0) {
    const userId = getCurrentUserId();
    await uploadLoanAttachmentsInternal(id, userId, normalizeUploadItems(files), 3);
  }

  // Finalize on the server: validates attachments and transitions status
  if (id) {
    await api.post(`/api/v1/client-loans/${id}/finalize`);
  }

  return { id, trackingNumber, loanId };
}

// Step 1: Create Loan Application (JSON only)
export async function createLoanApplicationJson(payload: any): Promise<{ id: number; trackingNumber?: number; loanId: string }> {
  // Use the same axios defaults (Authorization, Fineract-Platform-TenantId, Accept) as other authenticated calls
  const res = await api.post('/api/v1/client-loans', payload);

  // Try to extract loanId from common fields or headers
  const data = res.data || {};
  const headers = res.headers || {} as any;
  const loanIdRaw = data.loanId || data.id || data.applicationId || data.loanApplicationId || data.clientLoanId
    || headers['x-loan-id'] || headers['x-application-id']
    || findIdFallback(data);

  if (!loanIdRaw) {
    throw new Error('Unable to determine loanId from response');
  }
  const idNumeric = Number(data.id);
  const tracking = typeof data.trackingNumber === 'number' ? data.trackingNumber : undefined;
  return { id: isNaN(idNumeric) ? undefined as unknown as number : idNumeric, trackingNumber: tracking, loanId: String(loanIdRaw) };
}

// Update Loan Application
export async function updateLoanApplication(loanId: string, payload: any): Promise<{ loanId: string }> {
  const res = await api.put(`/api/v1/client-loans/${loanId}`, payload, {
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Try to extract loanId from common fields or headers
  const data = res.data || {};
  const headers = res.headers || {} as any;
  const updatedLoanId = data.loanId || data.id || data.applicationId || data.loanApplicationId || data.clientLoanId
    || headers['x-loan-id'] || headers['x-application-id']
    || findIdFallback(data);

  if (!updatedLoanId) {
    throw new Error('Unable to determine loanId from response');
  }
  return { loanId: updatedLoanId.toString() };
}

function findIdFallback(obj: any): string | undefined {
  try {
    const stack: any[] = [obj];
    while (stack.length) {
      const curr = stack.pop();
      if (curr && typeof curr === 'object') {
        for (const [k, v] of Object.entries(curr)) {
          if (/(^|_)id$|(^|_)number$/i.test(k) && (typeof v === 'string' || typeof v === 'number')) {
            return String(v);
          }
          if (v && typeof v === 'object') stack.push(v);
        }
      }
    }
  } catch {}
  return undefined;
}

function normalizeUploadItems(files: DocumentUploadItem[] | File[]): DocumentUploadItem[] {
  if (!files || files.length === 0) return [];
  if (files[0] instanceof File) {
    return (files as File[]).map((file) => ({
      file,
      fieldName: 'unknown',
      documentType: file.name.replace(/\.[^.]+$/, '') || 'Document',
    }));
  }
  return files as DocumentUploadItem[];
}

function appendUploadBatch(formData: FormData, batch: DocumentUploadItem[], fileFieldName: string) {
  batch.forEach((item) => {
    formData.append(fileFieldName, item.file);
    formData.append('documentTypes', item.documentType || '');
    formData.append('fieldNames', item.fieldName || '');
  });
}

// Step 2: Upload attachments in batches
export async function uploadLoanAttachmentsBatched(
  loanId: string | number,
  files: DocumentUploadItem[] | File[],
  batchSize: number = 3,
  trackingNumber?: string | number
): Promise<void> {
  const items = normalizeUploadItems(files);
  if (items.length === 0) return;
  // Prefer INTERNAL upload endpoint; keep function for legacy callers by delegating
  const idNumeric = typeof loanId === 'string' ? Number(loanId) : loanId;
  const userId = getCurrentUserId();
  if (!isNaN(idNumeric) && isFinite(idNumeric) && idNumeric > 0) {
    await uploadLoanAttachmentsInternal(idNumeric, userId, items, batchSize);
    return;
  }
  // Legacy external fallback (not recommended) - but now supports tracking number lookup
  const totalBatches = Math.ceil(items.length / batchSize);
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const formData = new FormData();
    // Only append loanId if it's valid, otherwise backend will use trackingNumber
    if (!isNaN(idNumeric) && isFinite(idNumeric) && idNumeric > 0) {
      formData.append('loanId', idNumeric.toString());
    }
    // Always append trackingNumber if provided as fallback
    if (trackingNumber !== undefined && trackingNumber !== null) {
      formData.append('trackingNumber', trackingNumber.toString());
    }
    formData.append('batchNumber', (Math.floor(i / batchSize) + 1).toString());
    formData.append('totalBatches', totalBatches.toString());
    appendUploadBatch(formData, batch, 'attachments');

    await api.post('/api/external/loan-application/attachments', formData, {
      headers: { 'X-API-Key': EXTERNAL_API_KEY }
    });
  }
}

// INTERNAL attachments upload
export async function uploadLoanAttachmentsInternal(
  loanNumericId: number,
  userId: string,
  files: DocumentUploadItem[] | File[],
  batchSize: number = 3
): Promise<void> {
  const items = normalizeUploadItems(files);
  if (items.length === 0) return;
  const totalBatches = Math.ceil(items.length / batchSize);
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const formData = new FormData();
    // Backend expects @RequestParam("files")
    appendUploadBatch(formData, batch, 'files');
    // Optional metadata (ignored by backend if not mapped)
    formData.append('batchNumber', (Math.floor(i / batchSize) + 1).toString());
    formData.append('totalBatches', totalBatches.toString());
    await api.post(`/api/v1/file-upload/loan/${loanNumericId}/user/${encodeURIComponent(userId)}`, formData);
  }
}

function getCurrentUserId(): string {
  try {
    const raw = localStorage.getItem('user_info');
    if (raw) {
      const user = JSON.parse(raw);
      return user.id || user.userId || user.sub || user.username || 'unknown';
    }
  } catch {}
  return 'unknown';
}

/**
 * Update documents for an existing loan (e.g., "needs more info" status).
 * This endpoint does NOT validate required documents - users can upload only missing documents.
 *
 * @param loanId The loan ID
 * @param files Array of files (or labeled upload items) to upload
 * @param batchSize Number of files per batch (default: 3)
 */
export async function updateLoanDocuments(
  loanId: string | number,
  files: DocumentUploadItem[] | File[],
  batchSize: number = 3
): Promise<void> {
  const items = normalizeUploadItems(files);
  if (items.length === 0) {
    throw new Error('At least one file must be provided');
  }

  const validLoanId = Number(loanId);
  if (isNaN(validLoanId) || validLoanId <= 0 || !isFinite(validLoanId)) {
    throw new Error('Invalid loan ID');
  }

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const formData = new FormData();

    // Backend expects @RequestParam("files")
    appendUploadBatch(formData, batch, 'files');

    // Use the new document update endpoint
    await api.post(`/api/v1/client-loans/${validLoanId}/documents/update`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
}
